import React, { forwardRef } from 'react';

export type ButtonVariant = 'btn-p' | 'btn-g' | 'btn-d' | 'primary' | 'ghost' | 'dark';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  loadingText?: string;
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'btn-p',
      size = 'md',
      isLoading = false,
      loadingText,
      fullWidth = false,
      leftIcon,
      rightIcon,
      children,
      className = '',
      disabled,
      type = 'button',
      ...props
    },
    ref,
  ) => {
    // Map variant prop to class
    let variantClass = 'btn-p';
    if (variant === 'btn-g' || variant === 'ghost') {
      variantClass = 'btn-g';
    } else if (variant === 'btn-d' || variant === 'dark') {
      variantClass = 'btn-d';
    } else if (variant === 'btn-p' || variant === 'primary') {
      variantClass = 'btn-p';
    }

    // Size styling adjustments
    let sizeClass = 'text-[14.5px] px-5 py-2.5';
    if (size === 'sm') {
      sizeClass = 'text-xs px-3 py-1.5 gap-1.5';
    } else if (size === 'lg') {
      sizeClass = 'text-base px-6 py-3.5 gap-2.5';
    }

    const widthClass = fullWidth ? 'w-full justify-center' : '';
    const isDisabled = disabled || isLoading;
    const disabledClass = isDisabled ? 'opacity-60 cursor-not-allowed pointer-events-none' : 'cursor-pointer';

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        className={`${variantClass} ${sizeClass} ${widthClass} ${disabledClass} ${className}`}
        {...props}
      >
        {isLoading ? (
          <>
            <svg
              className="animate-spin -ml-0.5 h-4 w-4 text-current"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span>{loadingText || children}</span>
          </>
        ) : (
          <>
            {leftIcon && <span className="inline-flex items-center">{leftIcon}</span>}
            {children}
            {rightIcon && <span className="inline-flex items-center">{rightIcon}</span>}
          </>
        )}
      </button>
    );
  },
);

Button.displayName = 'Button';
